import React, { useEffect, useState, Suspense } from "react";
import {
  Box,
  Button,
  Input,
  Flex,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  IconButton,
  Stack,
  Spinner,
  useToast,
  Image,
  Skeleton,
  SkeletonText,
} from "@chakra-ui/react";
import { AddIcon, DeleteIcon, EditIcon } from "@chakra-ui/icons";
import { Link as RouterLink } from "react-router-dom";
import { Link as ChakraLink } from "react-router-dom";
import axios from "axios";
import * as mod from "../../url";
import Sidebar from "../Sidebar";
import Navbar from "../Navbar/Navbar";

const storedData = JSON.parse(sessionStorage.getItem("superAdminLawvs"));
const token = storedData?.data?.token;
const config = { headers: { Authorization: `${token}` } };

const SIDEBAR_WIDTH = "250px";
const RECORDS_PER_PAGE = 20;

const ManageAllNews = () => {
  const [allNews, setAllNews] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const toast = useToast();

  // ✅ Fetch all news
  const fetchAllNews = async () => {
    try {
      const { data } = await axios.get(`${mod.api_url}/api/v1/news/news_get`);
      setAllNews(data.News);
    } catch (error) {
      console.error("Error fetching:", error);
      toast({
        title: "Failed to fetch news.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllNews();
  }, []);

  // ✅ Status Toggle
  const handleStatusToggle = async (slug, currentStatus) => {
    const newStatus = currentStatus === "active" ? "inactive" : "active";
    try {
      await axios.put(
        `${mod.api_url}/api/v1/news/news_status/${slug}`,
        { status: newStatus },
        config
      );

      setAllNews((prev) =>
        prev.map((n) => (n.slug === slug ? { ...n, status: newStatus } : n))
      );
    } catch (error) {
      console.error("Failed to update status", error);
    }
  };

  // ✅ Delete news
  const handleDelete = async (slug) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this news?"
    );
    if (!confirmDelete) return;

    try {
      await axios.delete(
        `${mod.api_url}/api/v1/news/news_delete/${slug}`,
        config
      );
      setAllNews((prev) => prev.filter((n) => n.slug !== slug));

      toast({
        title: "News deleted successfully.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Delete error:", error);
      toast({
        title: "Failed to delete news.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // ✅ Search Filter (case-insensitive)
  const filteredNews = allNews.filter((n) =>
    n?.title?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // ✅ Pagination
  const totalPages = Math.ceil(filteredNews.length / RECORDS_PER_PAGE);
  const startIdx = (currentPage - 1) * RECORDS_PER_PAGE;
  const currentRecords = filteredNews.slice(
    startIdx,
    startIdx + RECORDS_PER_PAGE
  );

  return (
    <>
      <Navbar />
      <Sidebar />
      <Box mt="70px" ml={{ base: 0, md: SIDEBAR_WIDTH }} p={3}>
        <Text fontSize="2xl" mb={4} bg="#D29B3F" p={3}>
          <strong>All News</strong>
        </Text>

        {/* 🔍 Search + Add Button */}
        <Flex
          bg="white"
          p={4}
          mb={4}
          borderRadius="md"
          boxShadow="sm"
          align="center"
          flexWrap="wrap"
          gap={4}
        >
          <Input
            placeholder="Search by title or keyword..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            maxW="300px"
          />
          <Flex ml="auto">
            <Button
              colorScheme="blue"
              leftIcon={<AddIcon />}
              as={RouterLink}
              to="/create-news"
              _hover={{ bg: "blue.100" }}
            >
              Add New Record
            </Button>
          </Flex>
        </Flex>

        <Box bg="white" p={4} borderRadius="md" boxShadow="sm">
          <Text fontSize="sm" mb={2} textAlign="right">
            Total Records: {filteredNews.length}
          </Text>

          {/* 🧩 Suspense Fallback with Skeleton */}
          <Suspense
            fallback={
              <Stack spacing={4}>
                {[...Array(5)].map((_, i) => (
                  <Box key={i} p={4} borderWidth="1px" borderRadius="md">
                    <Skeleton height="20px" width="30%" mb={2} />
                    <SkeletonText noOfLines={2} spacing="3" />
                  </Box>
                ))}
              </Stack>
            }
          >
            {loading ? (
              <Flex justify="center" align="center" py={10}>
                <Spinner size="xl" />
              </Flex>
            ) : (
              <>
                <Table
                  variant="simple"
                  size="sm"
                  style={{
                    border: "1px solid #ccc",
                    borderCollapse: "collapse",
                  }}
                >
                  <Thead bg="gray.100">
                    <Tr>
                      <Th border="1px solid #ccc">ID</Th>
                      <Th border="1px solid #ccc">Image</Th>
                      <Th border="1px solid #ccc">Title</Th>
                      <Th border="1px solid #ccc">Posted on</Th>
                      <Th border="1px solid #ccc">Edit</Th>
                      <Th border="1px solid #ccc">Status</Th>
                      <Th border="1px solid #ccc">Delete</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {currentRecords.map((news, index) => (
                      <Tr key={news._id}>
                        <Td border="1px solid #ccc">{startIdx + index + 1}</Td>
                        <Td border="1px solid #ccc">
                          <Image
                            src={news?.NewsImage}
                            alt="news"
                            width="120px"
                            height="80px"
                            // objectFit="cover"
                            borderRadius="md"
                          />
                        </Td>
                        <Td border="1px solid #ccc">
                          <ChakraLink
                            as={RouterLink}
                            // to={`/single-news/${news.slug}`}
                            color="blue.500"
                            _hover={{ textDecoration: "none" }}
                          >
                            {news?.title?.slice(0, 80)}
                          </ChakraLink>
                        </Td>
                        <Td border="1px solid #ccc">
                          {new Date(news.postedOn).toLocaleDateString()}
                        </Td>
                        <Td border="1px solid #ccc">
                          <ChakraLink
                            as={RouterLink}
                            to={`/update-news/${news.slug}`}
                          >
                            <IconButton
                              icon={<EditIcon />}
                              colorScheme="blue"
                              size="sm"
                            />
                          </ChakraLink>
                        </Td>
                        <Td border="1px solid #ccc">
                          <Button
                            size="sm"
                            colorScheme={
                              news.status === "active" ? "green" : "red"
                            }
                            onClick={() =>
                              handleStatusToggle(news.slug, news.status)
                            }
                          >
                            {news.status === "active" ? "Active" : "Inactive"}
                          </Button>
                        </Td>
                        <Td border="1px solid #ccc">
                          <IconButton
                            icon={<DeleteIcon />}
                            colorScheme="red"
                            size="sm"
                            onClick={() => handleDelete(news.slug)}
                          />
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>

                {/* 🔢 Pagination */}
                <Flex justify="space-between" align="center" mt={4}>
                  <Stack direction="row" spacing={2}>
                    {Array.from({ length: totalPages }).map((_, idx) => (
                      <Button
                        key={idx}
                        size="sm"
                        onClick={() => setCurrentPage(idx + 1)}
                        colorScheme={currentPage === idx + 1 ? "blue" : "gray"}
                        variant={currentPage === idx + 1 ? "solid" : "outline"}
                      >
                        {idx + 1}
                      </Button>
                    ))}
                  </Stack>
                </Flex>
              </>
            )}
          </Suspense>
        </Box>
      </Box>
    </>
  );
};

export default ManageAllNews;
